import { Check, CheckCircle2, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import EnterButton from "../buttons/EnterButton";

interface RevealedApiKeyDialogProps {
    apiKey: string | null;
    onClose: () => void;
}

export default function RevealedApiKeyDialog({ apiKey, onClose }: RevealedApiKeyDialogProps) {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!apiKey) setCopied(false);
    }, [apiKey]);

    const handleCopy = () => {
        if (!apiKey) return;
        navigator.clipboard.writeText(apiKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog open={apiKey !== null} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[720px] gap-6 rounded-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg font-medium">
                        <CheckCircle2 className="size-4" />
                        API key created
                    </DialogTitle>
                    <DialogDescription>
                        Copy it now.{" "}
                        <strong className="font-medium">
                            For security reasons this is the only time the full key is shown.
                        </strong>
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-2">
                    <code className="flex-1 break-all rounded border bg-background px-3 py-2 text-sm font-mono">
                        {apiKey}
                    </code>
                    <Button type="button" variant="outline" onClick={handleCopy}>
                        {copied ? <Check /> : <Copy />}
                        {copied ? "Copied" : "Copy"}
                    </Button>
                </div>

                <DialogFooter>
                    <EnterButton type="button" size="sm" onClick={onClose}>
                        Close
                    </EnterButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
